<template>
  <div class="admin-faqs-index p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">FAQs</h1>
        <p class="text-gray-600 mt-1">Manage frequently asked questions</p>
      </div>
      <Link
        :href="route('faqs.create')"
        class="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
      >
        <icon name="plus" class="w-5 h-5" />
        Create FAQ
      </Link>
    </div>

    <!-- Search -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-6">
      <form @submit.prevent="search" class="flex gap-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search FAQs..."
          class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
        <button
          type="submit"
          class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Search
        </button>
      </form>
    </div>

    <!-- FAQs Table -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="faq in faqs.data" :key="faq.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ faq.name }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-600 max-w-md">
                <div class="truncate" v-html="faq.details"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    faq.status ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  ]"
                >
                  {{ faq.status ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="flex items-center gap-2">
                  <Link
                    :href="route('faqs.edit', faq.id)"
                    class="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Edit
                  </Link>
                  <button
                    @click="deleteFaq(faq.id)"
                    class="text-red-600 hover:text-red-800 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="faqs.data.length === 0">
              <td colspan="4" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center gap-3">
                  <icon name="faq" class="w-16 h-16 text-gray-300" />
                  <p class="text-gray-500 text-lg font-medium">No FAQs found</p>
                  <Link
                    :href="route('faqs.create')"
                    class="text-green-600 hover:text-green-800 hover:underline"
                  >
                    Create your first FAQ
                  </Link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="faqs.links && faqs.links.length > 3" class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ faqs.from }} to {{ faqs.to }} of {{ faqs.total }} results
        </div>
        <div class="flex gap-2">
          <Link
            v-for="link in faqs.links"
            :key="link.label"
            :href="link.url || '#'"
            v-html="link.label"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              link.active
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300',
              !link.url ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    faqs: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: 'FAQs',
    },
  },
  data() {
    return {
      searchQuery: this.filters.search || '',
    }
  },
  methods: {
    search() {
      router.get(route('faqs'), { search: this.searchQuery }, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    deleteFaq(id) {
      if (confirm('Are you sure you want to delete this FAQ?')) {
        router.delete(route('faqs.destroy', id), {
          preserveScroll: true,
          onSuccess: () => {
            if (window.$toast) {
              window.$toast.success('FAQ deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-faqs-index {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>
